import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import {
  Card,
  Grid,
  CircularProgress,
  Icon,
  Tooltip,
  Box
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import { formatDate } from 'utils/DateTimeConversion';
import AddCaseModal from 'components/CustomModals/AddCaseModal';
import { Link } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { usePermission } from 'auth/usePermission';
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import TableActionMenu from 'components/TableActionMenu';
import { frontendLogger } from '../../utils/logger';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import useOpenCreateFromUrl from "hooks/useOpenCreateFromUrl";
import { User, Users } from "lucide-react";
import PageHeader from "components/v2/PageHeader";
import SegmentedControl from "components/v2/SegmentedControl";
import { useActiveApp } from "shell-v2/apps";

const ViewToggleIcon = styled(Box)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  backgroundColor: active ? 'white' : 'transparent',
  border: active ? 'none' : '1px solid rgba(255, 255, 255, 0.4)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: active ? 'white' : 'rgba(255, 255, 255, 0.15)',
    transform: 'translateY(-1px)',
  },
  '& svg': {
    fontSize: '20px',
    color: active ? theme.palette.info.main : 'white',
    transition: 'color 0.3s ease',
  },
  // v2: the strip is a white card header, so the toggle draws on white. The
  // repeated & outranks theme-v2's strip icon rule.
  ...(theme.pearlUi === 'v2' && {
    backgroundColor: active ? '#E6EFFB' : 'transparent',
    border: `1px solid ${active ? '#B9D0F0' : '#E2E7EF'}`,
    '&:hover': { backgroundColor: active ? '#E6EFFB' : '#F8FAFC', transform: 'none' },
    '&&&& svg': { color: `${active ? '#1453A0' : '#44546F'} !important` },
  }),
}));

const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

function Cases() {
  const { user, axiosInstance } = useAuth();
  const { canCreate } = usePermission('cases');
  // UI v2 layout (design-reference "Travel · Bookings list"): page header,
  // then the table in one card.
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { app } = useActiveApp();
  const casesLabel = app?.labels?.cases || 'Cases';
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCaseModalOpen, setIsCaseModalOpen] = useState(false);
  const [allCases, setAllCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    id: null
  });

  const [casesFilter, setCasesFilter] = useState(() => {
    return localStorage.getItem('casesFilter') || 'my';  // Ensure 'my' is default
  });

  const truncateString = (str, length) => {
    return str?.length > length ? str.substring(0, length) + '...' : str;
  };

  const columns = [
    {
      Header: "Case Number",
      accessor: "case_number",
      width: "12%",
      Cell: ({ value, row }) => (
        <MDTypography
          component="a"
          href="#"
          variant="caption"
          color="info"
          fontWeight="medium"
          sx={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            handleRowClick(row.original.id);
          }}
        >
          {value}
        </MDTypography>
      ),
    },
    {
      Header: "Subject",
      accessor: "subject",
      width: "8%",
      Cell: ({ value }) => (
          <MDTypography variant="caption" fontWeight="medium">
            {capitalizeFirstLetter(value)}
          </MDTypography>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      width: "8%",
      Cell: ({ value }) => (
          <MDTypography variant="caption" fontWeight="medium">
            {capitalizeFirstLetter(value)}
          </MDTypography>
      ),
    },
    {
      Header: "Internal Status",
      accessor: "internal_status",
      width: "14%",
      Cell: ({ value }) => (
          <MDTypography variant="caption" fontWeight="medium">
            {capitalizeFirstLetter(value)}
          </MDTypography>
      ),
    },
    {
      Header: "Category",
      accessor: "category",
      width: "12%",
      Cell: ({ value }) => (
          <MDTypography variant="caption" fontWeight="medium">
            {capitalizeFirstLetter(value)}
          </MDTypography>
      ),
    },
    {
      Header: "Priority",
      accessor: "priority",
      width: "8%",
      Cell: ({ value }) => (
          <MDTypography variant="caption" fontWeight="medium">
            {value}
          </MDTypography>
      ),
    },
    {
      Header: "Assigned To",
      accessor: "assigned_to_name",
      width: "15%",
      Cell: ({ row }) => (
        row.original.assigned_to ? (
          <MDTypography
            component={Link}
            to={`/users/${row.original.assigned_to}/profile`}
            variant="caption"
            color="info"
            fontWeight="medium"
            sx={{ cursor: 'pointer' }}
          >
            {capitalizeFirstLetter(row.original.assigned_to_name || 'Unassigned')}
          </MDTypography>
        ) : (
          <MDTypography variant="caption" color="text" fontWeight="regular">
            -
          </MDTypography>
        )
      ),
    },
    {
      Header: "Modified Date",
      accessor: "updated_at",
      width: "15%",
      Cell: ({ value }) => (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {formatDate(value)}
        </MDTypography>
      ),
      sortType: (rowA, rowB, columnId) => {
        const a = rowA.values[columnId];
        const b = rowB.values[columnId];

        // Handle cases where dates might be null/undefined
        if (!a && !b) return 0; // Both are null/undefined
        if (!a) return 1; // a is null/undefined, b is not
        if (!b) return -1; // b is null/undefined, a is not

        // Convert string dates to Date objects for comparison
        return new Date(b).getTime() - new Date(a).getTime(); // Descending order
      }
    },
    {
      Header: "Action",
      accessor: "actions",
      width: "8%",
      disableSortBy: true,
      Cell: ({ row }) => (
          <TableActionMenu
              row={row.original}
              onView={handleViewDetailedPage}
              onDelete={isDeleting ? undefined : handleDeleteEntry}
          />
      ),
    },
  ];

  // ----------------------------------------------------------------------
  // ACTION COLUMN - HELPER FUNCTIONS
  // ----------------------------------------------------------------------

  // View Detail Page Handler.

  const handleViewDetailedPage = (obj) => {
    navigate(`/cases/${obj.id}`);
  };

  // DELETE selected entry

  const handleDeleteEntry = (id) => {
    setDeleteConfirm({open: true, id: id})
  }

  // DELETE request handler

  const handleDeleteRequest = async () => {
    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/cases/${deleteConfirm.id}`);
      setSnackbar({open: true, message: 'Case deleted successfully', severity: 'success'})
      await fetchCases();
    } catch (error) {
      setSnackbar({open: true, message: 'Error deleting case', severity: 'error'})
    } finally {
      setIsDeleting(false);
      setDeleteConfirm({open: false, id: null})
    }
  }

  // ----------------------------------------------------------------------

  const handleCreateNewCase = () => {
    if (!canCreate) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create cases.',
        severity: 'error',
      });
      return;
    }
    setIsCaseModalOpen(true);
  };
  // v2 header "New case": the same dialog and the same gate as Add Case.
  useOpenCreateFromUrl(handleCreateNewCase, { allowed: canCreate });

  useEffect(() => {
    fetchCases();
  }, []);

  useEffect(() => {
    fetchCases();
  }, []);

  const handleCasesFilterChange = async (newFilter) => {
    if (newFilter === casesFilter) return; // Prevent unnecessary updates

    setCasesFilter(newFilter);
    localStorage.setItem('casesFilter', newFilter);
    setIsLoading(true);

    try {
      const queryParam = newFilter === 'my' ? '?all=true&my=true' : '?all=true&team=true';
      const response = await axiosInstance.get(`/cases/${queryParam}`);
      const transformedCases = transformCaseData(response.data);

      // Update all states in one go
      const filteredData = newFilter === 'my'
        ? transformedCases.filter(c => c.assigned_to === user.id)
        : transformedCases.filter(c => c.assigned_to !== user.id);

      setAllCases(transformedCases);
      setFilteredCases(filteredData);
      setCases(transformedCases);
    } catch (error) {
      console.error('Error fetching cases:', error);
      setSnackbar({
        open: true,
        message: 'Error loading cases. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCase = async (caseData) => {
    setIsLoading(true);
    try {
      // Get the next available case number
      const caseResponse = await axiosInstance.get('/cases/');
      const nextCaseNumber = caseResponse.data.length + 1;
      const uniqueCaseNumber = `CASE-${String(nextCaseNumber).padStart(5, '0')}`;

      // Create a new case with the generated case number
      const finalCaseData = {
        id: nextCaseNumber,
        ...caseData,
        case_number: uniqueCaseNumber,
      };

      const createResponse = await axiosInstance.post('/cases/', finalCaseData);

      setCases((prevCases) => [...prevCases, createResponse.data]);

      setSnackbar({
        open: true,
        message: 'Case created successfully!',
        severity: 'success',
      });

      setIsCaseModalOpen(false);
      fetchCases();

      await frontendLogger.logCreate('Case', { id: createResponse.data.id, action: 'create', created_by: user?.id, created_at: new Date().toISOString() });
    } catch (error) {
      console.error('Error saving case:', error);
      let errorMessage = 'Error creating case';
      if (error.response?.data?.detail) {
        errorMessage += `: ${Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail}`;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const transformCaseData = (serverData) => {
    return serverData.map(caseItem => ({
      id: caseItem.id,
      case_number: caseItem.case_number,
      account_name: caseItem.account_name,
      account_id: caseItem.account_id,
      contact_name: caseItem.contact_name,
      contact_id: caseItem.contact_id,
      subject: caseItem.subject,
      status: caseItem.status,
      internal_status: caseItem.internal_status,
      priority: caseItem.priority,
      category: caseItem.category,
      assigned_to: caseItem.assigned_to,
      assigned_to_name: caseItem.assigned_to_name || '-',
      created_at: caseItem.created_at,
      updated_at: caseItem.updated_at,
      created_by: caseItem.created_by,
      updated_by: caseItem.updated_by,
    }));
  };

  const fetchCases = async () => {
    setIsLoading(true);
    try {
      const queryParam = casesFilter === 'my' ? '?all=true&my=true' : '?all=true&team=true';
      const response = await axiosInstance.get(`/cases/${queryParam}`);
      const transformedCases = transformCaseData(response.data);

      // Update all states in one go to prevent multiple re-renders
      const filteredData = casesFilter === 'my'
        ? transformedCases.filter(c => c.assigned_to === user.id)
        : transformedCases.filter(c => c.assigned_to !== user.id);

      setAllCases(transformedCases);
      setFilteredCases(filteredData);
      setCases(transformedCases);
    } catch (error) {
      console.error('Error fetching cases:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching cases',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowClick = (caseId) => {
    navigate(`/cases/${caseId}`);
  };


  // if (isLoading) {
  //   return (
  //     <DashboardLayout>
  //       <DashboardNavbar />
  //       <MDBox display="flex" justifyContent="center" alignItems="center" height="80vh">
  //         <CircularProgress />
  //       </MDBox>
  //       
  //     </DashboardLayout>
  //   );
  // }
  // Check if the user is allowed to create cases
  // Support users are blocked via usePermission('cases').canCreate
  // Shared by the classic and v2 layouts below: the same actions and table.
  const listActions = (
    <>
      <MDButton
        variant="gradient"
        color="dark"
        onClick={handleCreateNewCase}
        sx={{ marginRight: 2 }}
        disabled={!canCreate}
      >
        <Icon sx={{ fontWeight: "bold" }}>add</Icon>
        Add Case
      </MDButton>
    </>
  );
  const listBody = (
    <>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <>
        {filteredCases.length > 0 ? (
          <DataTable
            table={{ columns, rows: filteredCases }} // Use filteredCases instead of cases
            isSorted={true}
            canSearch
            searchPlaceholder="Search for cases..."
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            showTotalEntries
            noEndBorder
            pagination={{ variant: "gradient", color: "info" }}
            exportCSV={true}
            defaultSortField="updated_at"
            defaultSortDirection="desc"
            initialState={{
              sortBy: [
                {
                  id: 'updated_at',
                  desc: true
                }
              ]
            }}
            objectType="case"
            onFilterApply={(conditions) => {
              console.log('Filter applied:', conditions);
              // Filtering is handled by DataTable component
            }}
            onFilterClear={() => {
              console.log('Filter cleared');
              fetchCases();
            }}
          />
        ): (
          <EmptyState 
            moduleName="Case" 
            filterType={casesFilter} 
            canCreate={canCreate} 
          />
        )}

        </>
      )}
    </>
  );

  const v2Main = (
    <MDBox>
      <PageHeader title={casesLabel} summary={isLoading ? null : `${filteredCases.length} ${filteredCases.length === 1 ? "case" : casesLabel.toLowerCase()} ${casesFilter === "my" ? "assigned to you" : "across your team"}`} actions={listActions} />
      <Card>
        <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1.5} px={2.5} pt={2.5}>
          <SegmentedControl
            label={`Whose ${casesLabel.toLowerCase()}`}
            value={casesFilter}
            onChange={handleCasesFilterChange}
            options={[
              { value: "my", label: "Mine", icon: User },
              { value: "team", label: "My team", icon: Users },
            ]}
          />
        </MDBox>
        <MDBox pt={1}>{listBody}</MDBox>
      </Card>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? v2Main : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  {/* Left Side: Cases title and Icons */}
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <MDTypography variant="h6" color="white">
                      Cases
                    </MDTypography>
                    <MDBox display="flex" gap={1}>
                      <Tooltip title="My Cases" placement="bottom">
                        <ViewToggleIcon
                          active={casesFilter === "my"}
                          onClick={() => handleCasesFilterChange("my")}
                        >
                          <Icon>person</Icon>
                        </ViewToggleIcon>
                      </Tooltip>
                      <Tooltip title="My Team's Cases" placement="bottom">
                        <ViewToggleIcon
                          active={casesFilter === "team"}
                          onClick={() => handleCasesFilterChange("team")}
                        >
                          <Icon>groups</Icon>
                        </ViewToggleIcon>
                      </Tooltip>
                    </MDBox>
                  </MDBox>

                  {/* Right Side: Add Case Button */}
                  <MDBox>
                    {listActions}
                  </MDBox>
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                {listBody}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      )}
      <AddCaseModal
        isOpen={isCaseModalOpen}
        onClose={() => setIsCaseModalOpen(false)}
        onSave={handleSaveCase}
      />

      {/* Delete Confirmation Dialog */}

      <Dialog
          open={deleteConfirm.open}
          onClose={() => !isDeleting && setDeleteConfirm({ open: false, id: null })}
      >
        <DialogTitle>Delete Order</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this Order? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
              onClick={() => setDeleteConfirm({ open: false, id: null })}
              disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
              onClick={handleDeleteRequest}
              color="error"
              variant="contained"
              disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      
    </DashboardLayout>
  );
}

export default Cases;